import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import api from '../services/api'
import { firebasePhoneAuth } from '../services/firebasePhoneAuth'
import { useAuth } from '../contexts/AuthContext'

// Country codes with flags and common countries
const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+1', country: 'USA/Canada', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+44', country: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+61', country: 'Australia', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+971', country: 'UAE', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+65', country: 'Singapore', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+86', country: 'China', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+81', country: 'Japan', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+82', country: 'South Korea', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: '+49', country: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+33', country: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+39', country: 'Italy', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: '+7', country: 'Russia', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: '+55', country: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '+27', country: 'South Africa', flag: '\u{1F1FF}\u{1F1E6}' },
]

// Auto-detect country code from browser locale
const detectCountryCode = (): string => {
  try {
    const locale = navigator.language || 'en-IN'
    const countryCode = locale.split('-')[1]?.toUpperCase()

    const countryMap: Record<string, string> = {
      'IN': '+91',
      'US': '+1',
      'GB': '+44',
      'AU': '+61',
      'AE': '+971',
      'SG': '+65',
      'CN': '+86',
      'JP': '+81',
      'KR': '+82',
      'DE': '+49',
      'FR': '+33',
      'IT': '+39',
      'RU': '+7',
      'BR': '+55',
      'ZA': '+27',
    }

    return countryMap[countryCode] || '+91' // Default to India
  } catch {
    return '+91' // Fallback to India
  }
}

// Turnstile site key — set via environment or leave empty to skip
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''

export default function LoginOTP() {
  const [step, setStep] = useState<'contact' | 'verify'>('contact')
  const [contact, setContact] = useState('')
  const [countryCode, setCountryCode] = useState(detectCountryCode())
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [contactType, setContactType] = useState<'email' | 'phone'>('email')
  const [otpTimer, setOtpTimer] = useState(0)
  const [otpExpiryTime, setOtpExpiryTime] = useState<number | null>(null)
  const [canResend, setCanResend] = useState(false)
  const [resendAttempts, setResendAttempts] = useState(0)
  const [formattedPhone, setFormattedPhone] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const recaptchaInitialized = useRef(false)
  const turnstileLoaded = useRef(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()

  // Get return URL from navigation state
  const from = (location.state as any)?.from || '/'

  // Auto-detect country code on mount
  useEffect(() => {
    const detected = detectCountryCode()
    setCountryCode(detected)
  }, [])

  // Initialize reCAPTCHA for Firebase Phone Auth
  useEffect(() => {
    if (!recaptchaInitialized.current) {
      recaptchaInitialized.current = true
      firebasePhoneAuth.initRecaptcha('recaptcha-container').then((result) => {
        if (result.success) {
          console.log('reCAPTCHA ready for phone auth')
        } else {
          console.warn('reCAPTCHA init issue:', result.message)
        }
      })
    }

    return () => {
      firebasePhoneAuth.cleanupRecaptcha()
      recaptchaInitialized.current = false
    }
  }, [])

  // Load Cloudflare Turnstile (Fix 4)
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || turnstileLoaded.current) return
    turnstileLoaded.current = true

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'
    script.async = true
    script.defer = true

    // Expose callback for Turnstile widget rendering
    ;(window as any).onTurnstileLoad = () => {
      if ((window as any).turnstile) {
        (window as any).turnstile.render('#turnstile-container', {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(null),
          theme: 'light',
          size: 'invisible',
        })
      }
    }

    document.head.appendChild(script)

    return () => {
      delete (window as any).onTurnstileLoad
    }
  }, [])

  // OTP countdown timer
  useEffect(() => {
    if (otpTimer > 0) {
      const interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [otpTimer])

  // Check OTP expiry
  useEffect(() => {
    if (otpExpiryTime && step === 'verify') {
      if (Date.now() < otpExpiryTime) {
        setError('')
      }

      const checkExpiry = setInterval(() => {
        const now = Date.now()
        if (now >= otpExpiryTime) {
          setError('OTP has expired. Please request a new one.')
          setCanResend(true)
          setOtpTimer(0)
        }
      }, 1000)

      return () => clearInterval(checkExpiry)
    }
  }, [otpExpiryTime, step])

  // Format timer display
  const formatTimer = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleSendOTP = async (isResend: boolean = false) => {
    // Rate limiting: Max 3 attempts in 10 minutes
    if (resendAttempts >= 3) {
      setError('Too many attempts. Please wait 10 minutes before trying again.')
      return
    }

    if (!contact.trim()) {
      setError('Please enter your email or phone number')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      // Determine if it's an email or phone
      const isEmail = contact.includes('@')
      const isPhone = /^\d{6,15}$/.test(contact.replace(/\s/g, ''))

      if (!isEmail && !isPhone) {
        setError('Please enter a valid email or phone number')
        setLoading(false)
        return
      }

      if (isPhone) {
        // Firebase Phone Auth — SMS sent via Firebase SDK (not our backend)
        setContactType('phone')

        let phone = contact.replace(/\s/g, '').replace(/^0/, '')
        phone = countryCode + phone
        setFormattedPhone(phone)

        console.log('Sending OTP via Firebase Phone Auth to:', phone)

        const result = await firebasePhoneAuth.sendOTP(phone)

        if (result.success) {
          console.log('OTP sent via Firebase Phone Auth')
          setSuccess(isResend ? 'New OTP sent! Check your messages.' : 'OTP sent! Check your phone messages.')

          setOtpTimer(60)
          setOtpExpiryTime(Date.now() + 5 * 60 * 1000)
          setCanResend(false)
          setResendAttempts(prev => prev + 1)

          setStep('verify')
        } else {
          setError(result.message || 'Failed to send OTP to phone')
        }
      } else {
        // Email OTP via Resend (backend: /auth/email/send-otp)
        setContactType('email')

        const result = await api.sendEmailOTP(contact, turnstileToken || undefined)

        if (result.success) {
          console.log('OTP sent to email via Resend')
          setSuccess(isResend ? 'New OTP sent! Check your inbox.' : 'OTP sent! Check your email inbox.')

          setOtpTimer(60)
          setOtpExpiryTime(Date.now() + 5 * 60 * 1000)
          setCanResend(false)
          setResendAttempts(prev => prev + 1)

          setStep('verify')
        } else {
          setError(result.message || 'Failed to send OTP to email')
        }
      }
    } catch (err) {
      console.error('Send OTP error:', err)
      setError('An error occurred while sending OTP')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP code')
      return
    }

    // Check if OTP has expired
    if (otpExpiryTime && Date.now() >= otpExpiryTime) {
      setError('OTP has expired. Please request a new one.')
      setCanResend(true)
      setOtpTimer(0)
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      let result: { success: boolean; token?: string; user?: any; message: string }

      if (contactType === 'phone') {
        // Step 1: Verify OTP with Firebase SDK (returns Firebase ID token)
        const firebaseResult = await firebasePhoneAuth.verifyOTP(otp)

        if (!firebaseResult.success || !firebaseResult.idToken) {
          setError(firebaseResult.message || 'Invalid OTP code')
          setLoading(false)
          return
        }

        // Step 2: Send Firebase ID token to our backend for verification + JWT
        result = await api.verifyFirebasePhone(firebaseResult.idToken)
      } else {
        // Email OTP: verify directly with backend
        result = await api.verifyEmailOTP(contact, otp)
      }

      if (result.success) {
        if (result.token && result.user) {
          setSuccess('Login successful! Redirecting...')

          login(result.token, result.user)

          setTimeout(() => {
            const user = result.user
            const isProfileComplete = user?.profileCompleted === true || user?.profileComplete === true

            if (isProfileComplete) {
              navigate(from, { replace: true })
            } else {
              navigate('/profile-setup', { state: { from }, replace: true })
            }
          }, 500)
        } else {
          console.error('API returned success but missing token or user:', result)
          setError('Authentication error: Please try again or contact support')
        }
      } else {
        setError(result.message || 'Invalid OTP code')
      }
    } catch (err) {
      console.error('Verify OTP error:', err)
      setError('An error occurred during verification')
    } finally {
      setLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (!canResend) {
      setError(`Please wait ${formatTimer(otpTimer)} before requesting a new OTP`)
      return
    }

    setOtp('')
    setError('')
    setSuccess('')

    if (contactType === 'phone' && formattedPhone) {
      // Re-init reCAPTCHA and resend via Firebase
      setLoading(true)
      try {
        const result = await firebasePhoneAuth.resendOTP(formattedPhone)
        if (result.success) {
          setSuccess('New OTP sent! Check your messages.')
          setOtpTimer(60)
          setOtpExpiryTime(Date.now() + 5 * 60 * 1000)
          setCanResend(false)
          setResendAttempts(prev => prev + 1)
        } else {
          setError(result.message || 'Failed to resend OTP')
        }
      } catch (err) {
        setError('Failed to resend OTP')
      } finally {
        setLoading(false)
      }
    } else {
      await handleSendOTP(true)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-onrecord-orange items-center justify-center">
        <div className="text-white">
          <img
            src="/logo.svg"
            alt="on record"
            className="w-64 h-64 mx-auto mb-8"
          />
          <h1 className="text-5xl font-bold text-center">on record</h1>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img
              src="/logo.svg"
              alt="on record"
              className="w-12 h-12 mb-4 lg:hidden"
            />
            <Link
              to="/login"
              className="inline-flex items-center text-gray-600 hover:text-gray-800 mb-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to login
            </Link>
            <h1 className="text-4xl font-bold mb-4">
              {step === 'contact' ? 'Sign in with OTP' : 'Verify OTP'}
            </h1>
            <p className="text-gray-600">
              {step === 'contact'
                ? 'Enter your email or phone number to receive a one-time password'
                : `We sent a code to ${contactType === 'phone' ? formattedPhone : contact}`
              }
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Contact Input Step */}
          {step === 'contact' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-2">
                  Email or Phone Number
                </label>

                <div className="flex gap-2">
                  {/* Country Code Dropdown - Only visible for phone numbers */}
                  {!contact.includes('@') && !/[a-zA-Z]/.test(contact) && (
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent bg-white"
                      disabled={loading}
                    >
                      {COUNTRY_CODES.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.flag} {item.code}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Input Field - switches between email and phone */}
                  <input
                    id="contact"
                    type="text"
                    value={contact}
                    onChange={(e) => {
                      const value = e.target.value
                      // If it's a phone number (only digits), filter non-digits
                      if (!value.includes('@') && /^\d/.test(value)) {
                        setContact(value.replace(/\D/g, ''))
                      } else {
                        setContact(value)
                      }
                    }}
                    placeholder="email@example.com or 9876543210"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent"
                    disabled={loading}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendOTP()}
                  />
                </div>

                {/* Helper text */}
                {contact && (
                  <p className="mt-2 text-xs text-gray-500">
                    {contact.includes('@')
                      ? `Email: ${contact}`
                      : /^\d/.test(contact)
                      ? `Phone: ${countryCode} ${contact}`
                      : 'Enter email or phone number'}
                  </p>
                )}
              </div>

              <button
                onClick={() => handleSendOTP()}
                className="w-full bg-onrecord-orange text-white font-bold py-3 rounded-full hover:bg-orange-600 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </div>
          )}

          {/* OTP Verification Step */}
          {step === 'verify' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP Code
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-onrecord-orange focus:border-transparent"
                  disabled={loading}
                  onKeyPress={(e) => e.key === 'Enter' && handleVerifyOTP()}
                  autoFocus
                />
              </div>

              <button
                onClick={handleVerifyOTP}
                className="w-full bg-onrecord-orange text-white font-bold py-3 rounded-full hover:bg-orange-600 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>

              {/* OTP Timer and Expiry Info */}
              <div className="text-center space-y-2">
                {otpExpiryTime && (
                  <div className="text-sm text-gray-600">
                    {otpTimer > 0 ? (
                      <span>Resend OTP in <span className="font-bold text-onrecord-orange">{formatTimer(otpTimer)}</span></span>
                    ) : otpExpiryTime > Date.now() ? (
                      <span className="text-green-600">OTP expires in <span className="font-bold">{formatTimer(Math.floor((otpExpiryTime - Date.now()) / 1000))}</span></span>
                    ) : (
                      <span className="text-red-600 font-bold">OTP has expired</span>
                    )}
                  </div>
                )}

                {canResend && (
                  <button
                    onClick={handleResendOTP}
                    className="text-sm font-medium text-onrecord-orange hover:underline cursor-pointer transition-colors"
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                )}

                {resendAttempts > 0 && (
                  <div className="text-xs text-gray-500">
                    Attempts: {resendAttempts}/3
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  setStep('contact')
                  setOtp('')
                  setError('')
                  setSuccess('')
                  setOtpTimer(0)
                  setOtpExpiryTime(null)
                  setCanResend(false)
                }}
                className="w-full text-gray-600 py-2 text-sm hover:text-gray-800"
                disabled={loading}
              >
                Change email/phone number
              </button>
            </div>
          )}

          {/* Cloudflare Turnstile container (invisible) */}
          <div id="turnstile-container" />

          {/* Firebase reCAPTCHA container (invisible) */}
          <div id="recaptcha-container" />

          {/* Footer Links */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs text-gray-500">
              <a href="/about" className="hover:underline">About</a>
              <a href="/terms" className="hover:underline">Terms</a>
              <a href="/privacy" className="hover:underline">Privacy</a>
              <a href="/cookies" className="hover:underline">Cookies</a>
            </div>
            <div className="mt-2 text-center text-xs text-gray-500">
              &copy; 2025 on record
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
